import { UserRoutes } from '~config/routes'
import {
    AI_RECEPTIONIST_SECTION_QUERY_PARAM,
    AiReceptionistSection,
    BookingTabIndex,
    EngagementTabIndex,
    GeneralTabIndex,
    SyncTabIndex,
} from '~components/settings/tab-indices'
import {
    AdminPermissionFeature,
    ClinicFeatureFieldsFragment,
} from '~graphql/generated/graphql'
import {
    UniversalSearchStaticItem,
    UniversalSearchVisibilityContext,
} from './types'

type ClinicFeatureKey = keyof Omit<ClinicFeatureFieldsFragment, '__typename'>

function hasFeature(
    context: UniversalSearchVisibilityContext,
    feature: ClinicFeatureKey,
): boolean {
    return Boolean(context.clinic?.features?.[feature])
}

function isAdmin(context: UniversalSearchVisibilityContext): boolean {
    return Boolean(context.isAdmin)
}

const clientKeywords = [
    'client',
    'clients',
    'customer',
    'customers',
    'pet owner',
    'owner',
    'new client',
    'new customer',
    'returning client',
    'existing client',
]

const patientKeywords = [
    'patient',
    'patients',
    'pet',
    'pets',
    'animal',
    'animals',
    'new patient',
    'returning patient',
    'existing patient',
]

const bookingKeywords = [
    'booking',
    'book',
    'online booking',
    'schedule',
    'scheduling',
    'appointment',
    'appointments',
    'availability',
    'calendar',
]

const pimsKeywords = [
    'pims',
    'pms',
    'integration',
    'integrations',
    'writeback',
    'write back',
    'sync',
    'syncing',
    'cornerstone',
    'ezyvet',
    'avimark',
    'daysmart',
    'impromed',
    'neo',
    'rhapsody',
    'shepherd',
]

const messageKeywords = [
    'message',
    'messages',
    'communication',
    'communications',
    'sms',
    'text',
    'texting',
    'email',
    'emails',
    'inbox',
    'chat',
]

const reviewKeywords = [
    'review',
    'reviews',
    'rating',
    'ratings',
    'feedback',
    'google',
    'facebook',
    'reputation',
]

const portalKeywords = [
    'portal',
    'patient portal',
    'client portal',
    'owner portal',
    'app',
]

const settingsKeywords = ['settings', 'setting', 'config', 'configuration']

const brandingKeywords = [
    'branding',
    'brand',
    'colors',
    'color',
    'theme',
    'look and feel',
    'booking page',
    'widget colors',
]

export const searchablePageItems = [
    {
        id: 'page.inbox',
        kind: 'page',
        title: 'Inbox',
        subtitle: 'Messages and client chats',
        route: UserRoutes.Chats,
        group: 'Messages',
        keywords: [...messageKeywords, 'conversation', 'conversations'],
        requiredPermission: AdminPermissionFeature.Messages,
        isVisible: context => hasFeature(context, 'messaging_enabled'),
    },
    {
        id: 'page.whiteboard',
        kind: 'page',
        title: 'Whiteboard',
        subtitle: 'Open clinic chat popovers',
        route: UserRoutes.Whiteboard,
        group: 'Messages',
        keywords: ['chat', 'whiteboard', 'conversations'],
        requiredPermission: AdminPermissionFeature.Whiteboard,
        isVisible: context => hasFeature(context, 'messaging_enabled'),
    },
    {
        id: 'page.daily-huddle',
        kind: 'page',
        title: 'Daily Huddle',
        subtitle: 'Daily clinic huddle',
        route: UserRoutes.DailyHuddle,
        group: 'Analytics',
        keywords: ['huddle', 'daily', 'team'],
        requiredPermission: AdminPermissionFeature.DailyHuddle,
        isVisible: context => hasFeature(context, 'huddle_enabled'),
    },
    {
        id: 'page.pending-actions',
        kind: 'page',
        title: 'Pending Actions',
        subtitle: 'Clinic follow-up tasks',
        route: UserRoutes.PendingActions,
        group: 'Clinic',
        keywords: ['tasks', 'follow up', 'pending', 'actions'],
        requiredPermission: AdminPermissionFeature.PendingActions,
        isVisible: context => hasFeature(context, 'scheduling_enabled'),
    },
    {
        id: 'page.team-chat',
        kind: 'page',
        title: 'Team Chat',
        subtitle: 'Internal team chat',
        route: UserRoutes.TeamChat,
        group: 'Messages',
        keywords: ['team', 'internal', 'chat'],
        requiredPermission: AdminPermissionFeature.TeamChat,
        isVisible: context => hasFeature(context, 'teamchat_enabled'),
    },
    {
        id: 'page.clients-patients',
        kind: 'page',
        title: 'Clients & Patients',
        subtitle: 'Client and patient records',
        route: UserRoutes.ClientsPatients,
        group: 'Clinic',
        keywords: [...clientKeywords, ...patientKeywords, 'records'],
    },
    {
        id: 'page.appointments',
        kind: 'page',
        title: 'Appointments',
        subtitle: 'Clinic appointments',
        route: UserRoutes.Appointments,
        group: 'Clinic',
        keywords: [...bookingKeywords, 'appt', 'visits'],
        requiredPermission: AdminPermissionFeature.Appointments,
        isVisible: context => hasFeature(context, 'scheduling_enabled'),
    },
    {
        id: 'page.clinic-info',
        kind: 'page',
        title: 'Clinic Info',
        subtitle: 'Clinic profile, hours, and contact details',
        route: UserRoutes.ClinicInfo,
        group: 'Clinic',
        keywords: ['clinic', 'info', 'hours', 'address', 'phone'],
        requiredPermission: AdminPermissionFeature.ClinicInfo,
    },
    {
        id: 'page.clinic-users',
        kind: 'page',
        title: 'Users',
        subtitle: 'Clinic users and roles',
        route: UserRoutes.ClinicUsers,
        group: 'Clinic',
        keywords: ['users', 'roles', 'permissions', 'staff', 'admins'],
        requiredPermission: AdminPermissionFeature.Users,
    },
    {
        id: 'page.data-sync',
        kind: 'page',
        title: 'Data Sync',
        subtitle: 'PIMS data sync status',
        route: UserRoutes.DataSync,
        group: 'Clinic',
        keywords: [...pimsKeywords, 'data', 'data sync'],
        isVisible: isAdmin,
    },
    {
        id: 'page.services',
        kind: 'page',
        title: 'Services',
        subtitle: 'Booking services',
        route: UserRoutes.Services,
        group: 'Booking',
        keywords: [
            'services',
            'appointment types',
            ...bookingKeywords,
            'new puppy exam',
            'exam',
            'wellness',
            'visit reason',
        ],
        requiredPermission: AdminPermissionFeature.Services,
    },
    {
        id: 'page.widgets',
        kind: 'page',
        title: 'Widgets',
        subtitle: 'Online booking widgets',
        route: UserRoutes.Widgets,
        group: 'Booking',
        keywords: [...bookingKeywords, 'widgets', 'booking widget', 'iframe'],
        requiredPermission: AdminPermissionFeature.Widgets,
        isVisible: context =>
            hasFeature(context, 'scheduling_enabled') ||
            hasFeature(context, 'messaging_enabled'),
    },
    {
        id: 'page.scheduling-rules',
        kind: 'page',
        title: 'Scheduling Rules',
        subtitle: 'Rules for online scheduling',
        route: UserRoutes.SchedulingRules,
        group: 'Booking',
        keywords: [
            ...bookingKeywords,
            'rules',
            'restrictions',
            'appointment rules',
        ],
        requiredPermission: AdminPermissionFeature.SchedulingRules,
        isVisible: context => hasFeature(context, 'scheduling_enabled'),
    },
    {
        id: 'page.availability-checker',
        kind: 'page',
        title: 'Availability Checker',
        subtitle: 'Check appointment openings',
        route: UserRoutes.AvailabilityChecker,
        group: 'Booking',
        keywords: [...bookingKeywords, 'slots', 'openings', 'times'],
        isVisible: context => hasFeature(context, 'scheduling_enabled'),
    },
    {
        id: 'page.clinicians',
        kind: 'page',
        title: 'Clinicians',
        subtitle: 'Booking clinicians',
        route: UserRoutes.Clinicians,
        group: 'Booking',
        keywords: [
            ...bookingKeywords,
            'clinicians',
            'doctors',
            'providers',
            'vets',
            'veterinarians',
        ],
        requiredPermission: AdminPermissionFeature.Clinicians,
        isVisible: context =>
            Boolean(context.clinic?.settings?.does_use_clinicians_to_schedule),
    },
    {
        id: 'page.rooms',
        kind: 'page',
        title: 'Rooms',
        subtitle: 'Booking rooms',
        route: UserRoutes.Rooms,
        group: 'Booking',
        keywords: [...bookingKeywords, 'rooms', 'resources', 'exam rooms'],
        requiredPermission: AdminPermissionFeature.Rooms,
        isVisible: context =>
            Boolean(context.clinic?.settings?.does_use_rooms_to_schedule),
    },
    {
        id: 'page.payments',
        kind: 'page',
        title: 'Payments',
        subtitle: 'Booking payments',
        route: UserRoutes.Payments,
        group: 'Booking',
        keywords: ['payments', 'billing', 'invoices'],
        requiredPermission: AdminPermissionFeature.Payments,
        isVisible: context => hasFeature(context, 'payments_enabled'),
    },
    {
        id: 'page.prescription-refills',
        kind: 'page',
        title: 'Prescription Refill Requests',
        subtitle: 'Online prescription refill requests',
        route: UserRoutes.PrescriptionRefillRequests,
        group: 'Booking',
        keywords: [
            ...bookingKeywords,
            'prescription',
            'prescriptions',
            'refills',
            'requests',
            'medications',
            'meds',
        ],
        isVisible: context => hasFeature(context, 'prescriptions_enabled'),
    },
    {
        id: 'page.audiences',
        kind: 'page',
        title: 'Audiences',
        subtitle: 'Client segments and filters',
        route: UserRoutes.Audiences,
        group: 'Engagement',
        keywords: [
            ...clientKeywords,
            'segments',
            'audiences',
            'customer segments',
            'filters',
            'rules',
            'targeting',
        ],
        requiredPermission: AdminPermissionFeature.ClientSegments,
        isVisible: context => hasFeature(context, 'notifs_enabled'),
    },
    {
        id: 'page.forms',
        kind: 'page',
        title: 'Forms',
        subtitle: 'Engagement forms',
        route: UserRoutes.Forms,
        group: 'Engagement',
        keywords: [
            ...pimsKeywords,
            'forms',
            'questionnaires',
            'form responses',
            'intake',
        ],
        requiredPermission: AdminPermissionFeature.Forms,
        isVisible: context => hasFeature(context, 'forms_enabled'),
    },
    {
        id: 'page.notifications',
        kind: 'page',
        title: 'Notifications',
        subtitle: 'Automated notifications',
        route: UserRoutes.Notifications,
        group: 'Engagement',
        keywords: [
            ...messageKeywords,
            ...reviewKeywords,
            'notifications',
            'reminders',
            'templates',
            'automation',
            'campaigns',
        ],
        requiredPermission: AdminPermissionFeature.Notifications,
        isVisible: context => hasFeature(context, 'notifs_enabled'),
    },
    {
        id: 'page.notification-logs',
        kind: 'page',
        title: 'Message Logs',
        subtitle: 'Notification and message delivery logs',
        route: UserRoutes.NotificationLogs,
        group: 'Engagement',
        keywords: ['logs', 'message logs', 'notification logs', 'delivery'],
        requiredPermission: AdminPermissionFeature.MessageLogs,
        isVisible: context => hasFeature(context, 'notifs_enabled'),
    },
    {
        id: 'page.mass-communication',
        kind: 'page',
        title: 'Bulk Messages',
        subtitle: 'Mass communication',
        route: UserRoutes.MassCommunication,
        group: 'Engagement',
        keywords: ['bulk messages', 'mass communication', 'broadcast'],
        requiredPermission: AdminPermissionFeature.BulkMessages,
        isVisible: context =>
            hasFeature(context, 'notifs_enabled') &&
            hasFeature(context, 'bulk_messaging_enabled'),
    },
    {
        id: 'page.campaigns',
        kind: 'page',
        title: 'Campaigns',
        subtitle: 'Engagement campaigns',
        route: UserRoutes.Campaigns,
        group: 'Engagement',
        keywords: ['campaigns', 'marketing', 'notifications'],
        requiredPermission: AdminPermissionFeature.Campaigns,
    },
    {
        id: 'page.reviews',
        kind: 'page',
        title: 'Reviews',
        subtitle: 'Review management',
        route: UserRoutes.Reviews,
        group: 'Engagement',
        keywords: ['reviews', 'ratings', 'google reviews', 'facebook reviews'],
        requiredPermission: AdminPermissionFeature.Reviews,
        isVisible: context => hasFeature(context, 'reviews_enabled'),
    },
    {
        id: 'page.message-templates',
        kind: 'page',
        title: 'Message Templates',
        subtitle: 'Reusable message templates',
        route: UserRoutes.MessageTemplates,
        group: 'Engagement',
        keywords: ['message templates', 'templates', 'sms', 'email'],
        requiredPermission: AdminPermissionFeature.MessageTemplates,
        isVisible: context => hasFeature(context, 'messaging_enabled'),
    },
    {
        id: 'page.email-templates',
        kind: 'page',
        title: 'Email Templates',
        subtitle: 'Reusable email templates',
        route: UserRoutes.EmailTemplates,
        group: 'Engagement',
        keywords: ['email templates', 'emails', 'templates'],
        requiredPermission: AdminPermissionFeature.EmailTemplates,
        isVisible: context => hasFeature(context, 'messaging_enabled'),
    },
    {
        id: 'page.compliance-tracker',
        kind: 'page',
        title: 'Compliance Tracker',
        subtitle: 'Engagement compliance tracking',
        route: UserRoutes.ComplianceTracker,
        group: 'Engagement',
        keywords: ['compliance', 'tracker', 'engagement'],
    },
    {
        id: 'page.loyalty',
        kind: 'page',
        title: 'Loyalty',
        subtitle: 'Loyalty program',
        route: UserRoutes.Loyalty,
        group: 'Engagement',
        keywords: ['loyalty', 'points', 'rewards'],
        requiredPermission: AdminPermissionFeature.Loyalty,
        isVisible: context => hasFeature(context, 'loyalty_enabled'),
    },
    {
        id: 'page.calls',
        kind: 'page',
        title: 'Calls',
        subtitle: 'Clinic calls',
        route: UserRoutes.Calls,
        group: 'Engagement',
        keywords: ['calls', 'phone', 'ai receptionist'],
        isVisible: context => hasFeature(context, 'calls_enabled'),
    },
    {
        id: 'page.shared-media',
        kind: 'page',
        title: 'Shared Media',
        subtitle: 'Reusable email media assets',
        route: UserRoutes.SharedMedia,
        group: 'Engagement',
        keywords: ['shared media', 'images', 'email media', 'assets'],
        requiredPermission: AdminPermissionFeature.EmailTemplates,
        isVisible: context => hasFeature(context, 'messaging_enabled'),
    },
    {
        id: 'page.portal-customization',
        kind: 'page',
        title: 'Patient Portal Customization',
        subtitle: 'Customize the patient portal',
        route: UserRoutes.PortalCustomization,
        group: 'Patient Portal',
        keywords: ['patient portal', 'portal', 'customization'],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
    },
    {
        id: 'page.portal-user-tracking',
        kind: 'page',
        title: 'Patient Portal User Tracking',
        subtitle: 'Portal and mobile app adoption',
        route: UserRoutes.UserTracking,
        group: 'Patient Portal',
        keywords: [
            'user tracking',
            'portal adoption',
            'app adoption',
            'patient portal',
            'mobile app',
        ],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
    },
    {
        id: 'page.portal-marketing',
        kind: 'page',
        title: 'Patient Portal Marketing Materials',
        subtitle: 'Portal marketing assets',
        route: UserRoutes.MarketingMaterials,
        group: 'Patient Portal',
        keywords: ['patient portal', 'marketing', 'materials'],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
    },
    {
        id: 'page.portal-pet-selfies',
        kind: 'page',
        title: 'Pet Selfies',
        subtitle: 'Review and display client pet photos',
        route: UserRoutes.PetSelfies,
        group: 'Patient Portal',
        keywords: [
            'pet selfies',
            'selfie',
            'slideshow',
            'patient portal',
            'photos',
        ],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
        isVisible: context => hasFeature(context, 'pet_selfies_enabled'),
    },
    {
        id: 'page.goals',
        kind: 'page',
        title: 'Goals',
        subtitle: 'Analytics goals',
        route: UserRoutes.Goals,
        group: 'Analytics',
        keywords: ['goals', 'analytics'],
        requiredPermission: AdminPermissionFeature.Goals,
        isVisible: context => hasFeature(context, 'huddle_enabled'),
    },
    {
        id: 'page.metrics',
        kind: 'page',
        title: 'Metrics',
        subtitle: 'Clinic metrics',
        route: UserRoutes.ClinicMetrics,
        group: 'Analytics',
        keywords: ['metrics', 'analytics', 'reporting'],
        requiredPermission: AdminPermissionFeature.Reporting,
        isVisible: context => hasFeature(context, 'huddle_enabled'),
    },
    {
        id: 'page.reporting',
        kind: 'page',
        title: 'Reporting',
        subtitle: 'Clinic reporting',
        route: UserRoutes.ClinicReporting,
        group: 'Analytics',
        keywords: ['reporting', 'reports', 'analytics'],
        requiredPermission: AdminPermissionFeature.Reporting,
        isVisible: context => hasFeature(context, 'huddle_enabled'),
    },
    {
        id: 'page.kpis',
        kind: 'page',
        title: 'KPIs',
        subtitle: 'KPI dashboard',
        route: UserRoutes.ClinicKpiDashboard,
        group: 'Analytics',
        keywords: ['kpis', 'analytics', 'dashboard'],
        requiredPermission: AdminPermissionFeature.Reporting,
    },
    {
        id: 'page.trends',
        kind: 'page',
        title: 'Trends',
        subtitle: 'Clinic trends',
        route: UserRoutes.ClinicTrends,
        group: 'Analytics',
        keywords: ['trends', 'analytics'],
        requiredPermission: AdminPermissionFeature.Trends,
        isVisible: context => hasFeature(context, 'huddle_enabled'),
    },
    {
        id: 'page.settings.general',
        kind: 'page',
        title: 'General Settings',
        subtitle: 'Clinic general settings',
        route: UserRoutes.GeneralSettings,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'general',
            'misc',
            'webhooks',
            'logout',
            'portal',
        ],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
    },
    {
        id: 'page.settings.general.general',
        kind: 'page',
        title: 'General',
        subtitle: 'General Settings > General',
        route: `${UserRoutes.GeneralSettings}?tab=${GeneralTabIndex.General}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'general settings',
            'inactivity',
            'timeout',
            'vaccinations',
            'portal',
        ],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
    },
    {
        id: 'page.settings.general.webhooks',
        kind: 'page',
        title: 'Webhooks',
        subtitle: 'General Settings > Webhooks',
        route: `${UserRoutes.GeneralSettings}?tab=${GeneralTabIndex.Webhooks}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'webhooks',
            'webhook',
            'api',
            'integrations',
            'callbacks',
        ],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
    },
    {
        id: 'page.settings.general.tmp-feature-flags',
        kind: 'page',
        title: 'Tmp Feature Flags',
        subtitle: 'General Settings > Tmp Feature Flags',
        route: `${UserRoutes.GeneralSettings}?tab=${GeneralTabIndex.TmpFeatureFlags}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'feature flags',
            'flags',
            'tmp',
            'experimental',
        ],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
        isVisible: isAdmin,
    },
    {
        id: 'page.settings.billing',
        kind: 'page',
        title: 'Billing Settings',
        subtitle: 'Clinic billing settings',
        route: UserRoutes.BillingSettings,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'billing',
            'subscription',
            'invoice',
            'payment method',
            'plan',
        ],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
    },
    {
        id: 'page.settings.booking',
        kind: 'page',
        title: 'Booking Settings',
        subtitle: 'Online booking configuration',
        route: UserRoutes.BookingSettings,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...bookingKeywords,
            ...brandingKeywords,
            'payments',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'page.settings.booking.booking',
        kind: 'page',
        title: 'Booking',
        subtitle: 'Booking Settings > Booking',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Booking}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...bookingKeywords,
            'online scheduling',
            'new clients',
            'new patients',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'page.settings.booking.branding',
        kind: 'page',
        title: 'Branding',
        subtitle: 'Booking Settings > Branding',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Branding}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...brandingKeywords,
            'primary color',
            'accent color',
            'secondary color',
            'secondary',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'page.settings.booking.payments',
        kind: 'page',
        title: 'Payments',
        subtitle: 'Booking Settings > Payments',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Payments}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'payments',
            'payment writeback',
            'billing',
            'invoices',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'page.settings.engagement',
        kind: 'page',
        title: 'Engagement Settings',
        subtitle: 'Messaging, reviews, and loyalty settings',
        route: UserRoutes.EngagementSettings,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...messageKeywords,
            ...reviewKeywords,
            'engagement',
            'loyalty',
            'ai receptionist',
            'writeback',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'page.settings.engagement.communications',
        kind: 'page',
        title: 'Communications',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...messageKeywords,
            ...pimsKeywords,
            'writeback',
            'auto reply',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'page.settings.engagement.ai-receptionist',
        kind: 'page',
        title: 'AI Receptionist',
        subtitle: 'Engagement Settings > AI Receptionist',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.AIReceptionist}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'ai receptionist',
            'voice',
            'phone',
            'calls',
            'adaptive voice speed',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'page.settings.engagement.ai-receptionist-instructions',
        kind: 'page',
        title: 'AI Receptionist instructions',
        subtitle: 'Engagement Settings > AI Receptionist > Instructions',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.AIReceptionist}&${AI_RECEPTIONIST_SECTION_QUERY_PARAM}=${AiReceptionistSection.Instructions}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'ai receptionist',
            'instructions',
            'custom instructions',
            'playbook',
            'call playbook',
            'greeting',
            'prompt',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'page.settings.engagement.chat-statuses',
        kind: 'page',
        title: 'Chat Statuses',
        subtitle: 'Engagement Settings > Chat Statuses',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.ChatStatuses}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...messageKeywords,
            'chat statuses',
            'status',
            'tags',
            'labels',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'page.settings.engagement.custom-merge-tags',
        kind: 'page',
        title: 'Custom Merge Tags',
        subtitle: 'Engagement Settings > Custom Merge Tags',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.CustomMergeTags}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'merge tags',
            'custom tags',
            'templates',
            'variables',
            'placeholders',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'page.settings.engagement.loyalty',
        kind: 'page',
        title: 'Loyalty',
        subtitle: 'Engagement Settings > Loyalty',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Loyalty}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            'loyalty',
            'points',
            'rewards',
            'invoices',
            'referrals',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'page.settings.engagement.reviews',
        kind: 'page',
        title: 'Reviews',
        subtitle: 'Engagement Settings > Reviews',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Reviews}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...reviewKeywords,
            'private reviews',
            'google reviews',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'page.settings.sync',
        kind: 'page',
        title: 'Sync Settings',
        subtitle: 'PIMS sync configuration',
        route: UserRoutes.SyncSettings,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...pimsKeywords,
            'appointment statuses',
            'products',
            'species',
            'sexes',
            'reminder types',
        ],
        requiredPermission: AdminPermissionFeature.SyncSettings,
    },
    {
        id: 'page.settings.sync.sync',
        kind: 'page',
        title: 'Sync',
        subtitle: 'Sync Settings > Sync',
        route: `${UserRoutes.SyncSettings}?tab=${SyncTabIndex.Sync}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...pimsKeywords,
            'pims reminders',
            'reminders',
        ],
        requiredPermission: AdminPermissionFeature.SyncSettings,
    },
    {
        id: 'page.settings.sync.appointment-statuses',
        kind: 'page',
        title: 'Appointment Statuses',
        subtitle: 'Sync Settings > Appointment Statuses',
        route: `${UserRoutes.SyncSettings}?tab=${SyncTabIndex.AppointmentStatuses}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...pimsKeywords,
            ...bookingKeywords,
            'appointment statuses',
            'status mapping',
            'statuses',
        ],
        requiredPermission: AdminPermissionFeature.SyncSettings,
    },
    {
        id: 'page.settings.sync.products',
        kind: 'page',
        title: 'Products',
        subtitle: 'Sync Settings > Products',
        route: `${UserRoutes.SyncSettings}?tab=${SyncTabIndex.Products}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...pimsKeywords,
            'products',
            'inventory',
            'items',
        ],
        requiredPermission: AdminPermissionFeature.SyncSettings,
    },
    {
        id: 'page.settings.sync.sexes',
        kind: 'page',
        title: 'Sexes',
        subtitle: 'Sync Settings > Sexes',
        route: `${UserRoutes.SyncSettings}?tab=${SyncTabIndex.Sexes}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...pimsKeywords,
            ...patientKeywords,
            'sexes',
            'gender',
            'genders',
            'male',
            'female',
            'neutered',
            'spayed',
        ],
        requiredPermission: AdminPermissionFeature.SyncSettings,
    },
    {
        id: 'page.settings.sync.reminder-types',
        kind: 'page',
        title: 'Reminder Types',
        subtitle: 'Sync Settings > Reminder Types',
        route: `${UserRoutes.SyncSettings}?tab=${SyncTabIndex.ReminderTypes}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...pimsKeywords,
            'reminder types',
            'reminders',
            'recalls',
            'compliance',
        ],
        requiredPermission: AdminPermissionFeature.SyncSettings,
    },
    {
        id: 'page.settings.sync.species',
        kind: 'page',
        title: 'Species',
        subtitle: 'Sync Settings > Species',
        route: `${UserRoutes.SyncSettings}?tab=${SyncTabIndex.Species}`,
        group: 'Settings',
        keywords: [
            ...settingsKeywords,
            ...pimsKeywords,
            ...patientKeywords,
            'species',
            'dog',
            'cat',
            'canine',
            'feline',
        ],
        requiredPermission: AdminPermissionFeature.SyncSettings,
    },
] satisfies readonly UniversalSearchStaticItem[]

export const searchableSettingItems = [
    {
        id: 'setting.engagement.form-writeback',
        kind: 'setting',
        title: 'Enable form writeback to the PIMS?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...pimsKeywords,
            'form writeback',
            'forms',
            'forms not syncing',
            'write forms to pims',
            'medical record',
            'record writeback',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.communication-writeback',
        kind: 'setting',
        title: 'Enable communication writeback to the PIMS?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...pimsKeywords,
            ...messageKeywords,
            'communication writeback',
            'message writeback',
            'messages not writing back',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.instant-writeback-sent',
        kind: 'setting',
        title: 'Instantly write back messages when sent?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...pimsKeywords,
            ...messageKeywords,
            'sent messages',
            'outbound messages',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.instant-writeback-received',
        kind: 'setting',
        title: 'Instantly write back messages when received?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...pimsKeywords,
            ...messageKeywords,
            'writeback',
            'received messages',
            'incoming messages',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.ai-writeback',
        kind: 'setting',
        title: 'Use AI to decide to write back messages?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...pimsKeywords,
            ...messageKeywords,
            'ai writeback',
            'message writeback',
            'smart writeback',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.notification-writeback',
        kind: 'setting',
        title: 'Write back system notification messages?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...pimsKeywords,
            ...messageKeywords,
            'notification writeback',
            'system notifications',
            'automated notifications',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.sms-confirmation',
        kind: 'setting',
        title: 'Prevent clients from confirming appointments via SMS?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...messageKeywords,
            ...bookingKeywords,
            'sms confirmation',
            'confirm',
            'yes reply',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.auto-reply',
        kind: 'setting',
        title: 'Enable auto-reply for incoming messages?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...messageKeywords,
            'auto reply',
            'automatic reply',
            'away message',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.unreplied-message-alert',
        kind: 'setting',
        title: 'Alert the clinic when a client message goes unreplied?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...messageKeywords,
            'unreplied',
            'unanswered',
            'no reply',
            'response time',
            'reply reminder',
            'escalate',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.message-priority',
        kind: 'setting',
        title: 'Use automatic message priority?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...messageKeywords,
            'message priority',
            'automatic priority',
            'urgent messages',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.auto-assign-patient',
        kind: 'setting',
        title: 'Automatically assign most likely patient to messages?',
        subtitle: 'Engagement Settings > Communications',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Communications}`,
        group: 'Settings',
        keywords: [
            ...messageKeywords,
            ...patientKeywords,
            'assign patient',
            'patient matching',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.ai-receptionist-speed',
        kind: 'setting',
        title: 'Adaptive voice speed',
        subtitle: 'Engagement Settings > AI Receptionist > Voice & setup',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.AIReceptionist}&${AI_RECEPTIONIST_SECTION_QUERY_PARAM}=${AiReceptionistSection.VoiceAndSetup}`,
        group: 'Settings',
        keywords: ['ai receptionist', 'voice', 'speed', 'phone'],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.loyalty-invoices',
        kind: 'setting',
        title: 'Should award loyalty for invoices?',
        subtitle: 'Engagement Settings > Loyalty',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Loyalty}`,
        group: 'Settings',
        keywords: ['loyalty', 'invoices', 'points'],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.private-reviews',
        kind: 'setting',
        title: 'Allow private reviews?',
        subtitle: 'Engagement Settings > Reviews',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Reviews}`,
        group: 'Settings',
        keywords: [...reviewKeywords, 'private reviews', 'internal feedback'],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.review-notifications',
        kind: 'setting',
        title: 'Send review notifications?',
        subtitle: 'Engagement Settings > Reviews',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Reviews}`,
        group: 'Settings',
        keywords: [
            ...reviewKeywords,
            ...messageKeywords,
            'review notifications',
            'review requests',
        ],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.engagement.google-reviews',
        kind: 'setting',
        title: 'Allow Google reviews?',
        subtitle: 'Engagement Settings > Reviews',
        route: `${UserRoutes.EngagementSettings}?tab=${EngagementTabIndex.Reviews}`,
        group: 'Settings',
        keywords: [...reviewKeywords, 'google reviews', 'google link'],
        requiredPermission: AdminPermissionFeature.EngagementSettings,
    },
    {
        id: 'setting.booking.disable-online-scheduling',
        kind: 'setting',
        title: 'Disable online scheduling?',
        subtitle: 'Booking Settings > Booking',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Booking}`,
        group: 'Settings',
        keywords: [
            ...bookingKeywords,
            'disable scheduling',
            'turn off booking',
            'stop online booking',
            'hide booking',
            'enable scheduling',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.booking.allow-new-patients',
        kind: 'setting',
        title: 'Allow new patients to book online?',
        subtitle: 'Booking Settings > Booking',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Booking}`,
        group: 'Settings',
        keywords: [
            ...bookingKeywords,
            ...patientKeywords,
            'first visit',
            'new pet',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.booking.allow-new-clients',
        kind: 'setting',
        title: 'Allow new clients to book online?',
        subtitle: 'Booking Settings > Booking',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Booking}`,
        group: 'Settings',
        keywords: [
            ...bookingKeywords,
            ...clientKeywords,
            'first visit',
            'new customer',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.booking.require-phone-verification',
        kind: 'setting',
        title: 'Require phone verification when booking?',
        subtitle: 'Booking Settings > Booking',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Booking}`,
        group: 'Settings',
        keywords: ['phone verification', 'booking', 'verify'],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.booking.require-new-client-address',
        kind: 'setting',
        title: 'Require new clients to enter their address?',
        subtitle: 'Booking Settings > Booking',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Booking}`,
        group: 'Settings',
        keywords: [...bookingKeywords, ...clientKeywords, 'address', 'home'],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.booking.require-new-client-birthday',
        kind: 'setting',
        title: 'Require new clients to enter their birthday?',
        subtitle: 'Booking Settings > Booking',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Booking}`,
        group: 'Settings',
        keywords: [
            ...bookingKeywords,
            ...clientKeywords,
            'birthday',
            'date of birth',
            'dob',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.booking.prescription-refills',
        kind: 'setting',
        title: 'Enable instant prescription refill requests?',
        subtitle: 'Booking Settings > Booking',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Booking}`,
        group: 'Settings',
        keywords: ['prescription', 'refill', 'instant requests'],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.booking.primary-color',
        kind: 'setting',
        title: 'Primary Color',
        subtitle: 'Booking Settings > Branding',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Branding}`,
        group: 'Settings',
        keywords: [
            ...brandingKeywords,
            'primary color',
            'primary',
            'brand color',
            'hex',
            'main color',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.booking.accent-color',
        kind: 'setting',
        title: 'Accent Color',
        subtitle: 'Booking Settings > Branding',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Branding}`,
        group: 'Settings',
        keywords: [
            ...brandingKeywords,
            'accent color',
            'accent',
            'secondary color',
            'secondary',
            'highlight color',
            'hex',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.booking.payment-writeback',
        kind: 'setting',
        title: 'Enable automatic payment writeback?',
        subtitle: 'Booking Settings > Payments',
        route: `${UserRoutes.BookingSettings}?tab=${BookingTabIndex.Payments}`,
        group: 'Settings',
        keywords: [
            ...pimsKeywords,
            'payment writeback',
            'payments',
            'invoices',
            'billing',
        ],
        requiredPermission: AdminPermissionFeature.BookingSettings,
    },
    {
        id: 'setting.general.inactivity-timeout',
        kind: 'setting',
        title: 'Enable automatic logout after inactivity?',
        subtitle: 'General Settings > General',
        route: `${UserRoutes.GeneralSettings}?tab=${GeneralTabIndex.General}`,
        group: 'Settings',
        keywords: ['logout', 'inactivity', 'timeout', 'security'],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
    },
    {
        id: 'setting.general.portal-vaccinations',
        kind: 'setting',
        title: 'Show vaccinations in the portal?',
        subtitle: 'General Settings > General',
        route: `${UserRoutes.GeneralSettings}?tab=${GeneralTabIndex.General}`,
        group: 'Settings',
        keywords: [
            ...portalKeywords,
            'vaccinations',
            'vaccines',
            'immunizations',
            'shot records',
        ],
        requiredPermission: AdminPermissionFeature.GeneralSettings,
    },
    {
        id: 'setting.sync.pims-reminders',
        kind: 'setting',
        title: 'Sync PIMS reminders?',
        subtitle: 'Sync Settings > Sync',
        route: `${UserRoutes.SyncSettings}?tab=${SyncTabIndex.Sync}`,
        group: 'Settings',
        keywords: [
            ...pimsKeywords,
            'pims reminders',
            'reminders',
            'compliance reminders',
            'recalls',
        ],
        requiredPermission: AdminPermissionFeature.SyncSettings,
    },
] satisfies readonly UniversalSearchStaticItem[]

export const searchableStaticItems = [
    ...searchablePageItems,
    ...searchableSettingItems,
] satisfies readonly UniversalSearchStaticItem[]
